/** The keyboard cheat sheet (opened with ?): every shortcut by group, searchable, with key caps for this
 * platform, and rebinding (click a key, press the new one; a key another action uses is refused, never stolen). */
import { Keyboard, RotateCcw, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "../i18n/I18nProvider";
import { ACTIONS, actionLabelKey, type ActionDef, comboParts, eventCombo, findConflicts, groupLabelKey, isWheelCombo, rebind, resetBinding, resolveBindings, SHEET_COLUMNS, wheelCombo, type Group, type Overrides, type RebindResult } from "./hotkeys.ts";


export function Keys({ combo, mac }: { combo: string; mac: boolean }) {
  return (
    <span className="kbd-combo" dir="ltr">
      {comboParts(combo, mac).map((part, i) => (
        <kbd key={i}>{part}</kbd>
      ))}
    </span>
  );
}

/** `groups`: only these groups (the Permit workspace shows its own keys and Help). */
export function CheatSheet({ mac, overrides, onOverrides, onClose, groups }: { mac: boolean; overrides: Overrides; onOverrides: (o: Overrides) => void; onClose: () => void; groups?: Group[] }) {
  const t = useT();
  const refusal = (result: Extract<RebindResult, { ok: false }>) =>
    result.reason === "taken" && result.conflictWith ? t("editor.hotkeys.rebind.taken", { action: t(actionLabelKey(result.conflictWith)) }) : t(`editor.hotkeys.rebind.${result.reason}` as const);
  const COLUMNS = groups ? SHEET_COLUMNS.map((c) => c.filter((g) => groups.includes(g))).filter((c) => c.length) : SHEET_COLUMNS;
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const bindings = useMemo(() => resolveBindings(overrides), [overrides]);
  const conflicts = useMemo(() => findConflicts(bindings), [bindings]);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => searchRef.current?.focus(), []);

  // While rebinding, the next key press is the new shortcut (Esc cancels); a wheel gesture takes the next turn of
  // the wheel with the modifiers held.
  useEffect(() => {
    if (!editing) return;
    const wheelAction = ACTIONS.find((a) => a.id === editing)?.keys.some(isWheelCombo);
    const onWheel = (e: WheelEvent) => {
      if (!wheelAction) return;
      e.preventDefault();
      e.stopPropagation();
      const result = rebind(overrides, editing, wheelCombo(e, mac));
      if (result.ok) {
        onOverrides(result.overrides);
        setMessage(null);
        setEditing(null);
      } else setMessage(refusal(result));
    };
    window.addEventListener("wheel", onWheel, { capture: true, passive: false });
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (["Shift", "Control", "Alt", "Meta"].includes(e.key)) return;
      if (e.key === "Escape") {
        setEditing(null);
        setMessage(null);
        return;
      }
      const result = rebind(overrides, editing, eventCombo(e, mac));
      if (result.ok) {
        onOverrides(result.overrides);
        setMessage(null);
        setEditing(null);
      } else setMessage(refusal(result));
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("wheel", onWheel, { capture: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, overrides, mac, onOverrides, t]);

  const q = query.trim().toLowerCase();
  const matches = ACTIONS.filter((a) => !q || t(actionLabelKey(a.id)).toLowerCase().includes(q) || (bindings.get(a.id) ?? []).some((k) => k.includes(q)));
  // actions sharing a sheet row (the nudge directions) show as one row with all their keys
  const visible: (ActionDef & { merged?: string[] })[] = [];
  for (const a of matches) {
    const row = a.sheetRow ? visible.find((v) => v.sheetRow === a.sheetRow) : undefined;
    if (row) row.merged = [...(row.merged ?? [row.id]), a.id];
    else visible.push({ ...a });
  }
  return (
    <div className="cheat-backdrop" onPointerDown={onClose} role="presentation">
      <div className="cheat-sheet" role="dialog" aria-modal="true" aria-label={t("editor.cheatSheet.title")} onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <Keyboard size={18} />
          <b>{t("editor.cheatSheet.title")}</b>
          <div className="input-icon cheat-search">
            <Search />
            <input
              ref={searchRef}
              className="input"
              placeholder={t("editor.cheatSheet.search")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && (query ? setQuery("") : onClose())}
            />
          </div>
          <button className="icon-btn" onClick={onClose} aria-label={t("editor.cheatSheet.close")}>
            <X size={18} />
          </button>
        </header>
        <div className="cheat-body">
          {COLUMNS.map((groups, column) => (
          <div className="cheat-column" key={column}>
          {groups.map((group) => {
            const rows = visible.filter((a) => a.group === group);
            if (!rows.length) return null;
            return (
              <section key={group}>
                <h3>{t(groupLabelKey(group))}</h3>
                {rows.map((a) => {
                  const keys = (a.merged ?? [a.id]).flatMap((id) => bindings.get(id) ?? []);
                  const clash = conflicts.some((c) => c.actions.includes(a.id));
                  return (
                    <div key={a.id} className={`cheat-row${editing === a.id ? " editing" : ""}${clash ? " clash" : ""}`}>
                      <span>{t(actionLabelKey(a.id))}</span>
                      <span className="cheat-keys">
                        {a.gesture && <span className="cheat-gesture">{t(a.gesture)}</span>}
                        {editing === a.id ? (
                          <span className="cheat-press">{a.keys.some(isWheelCombo) ? t("editor.cheatSheet.pressWheel") : t("editor.cheatSheet.pressKey")}</span>
                        ) : (
                          keys.map((k, i) => (
                            <span key={k} className="row" style={{ gap: 4 }}>
                              {i > 0 && !a.merged && <span className="faint">{t("editor.cheatSheet.or")}</span>}
                              <Keys combo={k} mac={mac} />
                            </span>
                          ))
                        )}
                        {a.keys.length > 0 && !a.gesture && !a.merged && (
                          <button className="cheat-edit" onClick={() => { setEditing(editing === a.id ? null : a.id); setMessage(null); }} title={t("editor.cheatSheet.changeTitle")}>
                            {editing === a.id ? t("editor.cheatSheet.cancel") : t("editor.cheatSheet.change")}
                          </button>
                        )}
                        {overrides[a.id] && (
                          <button className="cheat-edit" onClick={() => onOverrides(resetBinding(overrides, a.id))} title={t("editor.cheatSheet.resetTitle")} aria-label={t("editor.cheatSheet.reset")}>
                            <RotateCcw size={12} />
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </section>
            );
          })}
          </div>
          ))}
        </div>
        <footer>
          {message ? <span className="warn-text">{message}</span> : <span className="muted small">{mac ? t("editor.cheatSheet.footer.mac") : t("editor.cheatSheet.footer.pc")}</span>}
          {Object.keys(overrides).length > 0 && (
            <button className="cheat-edit" onClick={() => onOverrides({})}>
              {t("editor.cheatSheet.resetAll")}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
